/**
 * AI Presets Management Page
 * Admin control panel for managing AI presets and their ranking weights
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Slider } from '../../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Sparkles, ArrowLeft, Save, RefreshCw, Eye, EyeOff,
  Car, Truck, Bike, Ship, Tractor, Bus, Zap,
  Settings, AlertTriangle, CheckCircle, Edit2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Vehicle type icons
const VEHICLE_ICONS = {
  cars: Car,
  vans: Truck,
  buses: Bus,
  'motorcycles-scooters': Bike,
  'trucks-lorries': Truck,
  'three-wheelers': Car,
  'boats-watercraft': Ship,
  'electric-bikes': Zap,
  'farm-vehicles': Tractor,
  'plant-construction': Tractor,
  motorhomes: Truck,
  caravans: Truck
};

// Ranking weight labels
const WEIGHT_LABELS = {
  relevance_score: { label: 'Relevance', icon: '🎯', description: 'Match to search/filters' },
  price_fairness_score: { label: 'Price Fairness', icon: '💰', description: 'Value for money' },
  trust_score: { label: 'Trust', icon: '✅', description: 'Seller verification & quality' },
  listing_quality_score: { label: 'Listing Quality', icon: '⭐', description: 'Photos & description' },
  freshness_score: { label: 'Freshness', icon: '🆕', description: 'Recently posted' },
  distance_score: { label: 'Distance', icon: '📍', description: 'Proximity to user' },
  seller_responsiveness_score: { label: 'Responsiveness', icon: '💬', description: 'Reply time' }
};

const AIPresetsManagementPage = () => {
  const [presets, setPresets] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState('cars');
  const [globalSettings, setGlobalSettings] = useState({
    presetsEnabled: true,
    rankingEnabled: true,
    showRankingScores: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [editDialog, setEditDialog] = useState(false);

  useEffect(() => {
    fetchPresets();
    fetchGlobalSettings();
  }, []);

  const fetchPresets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/presets`);
      if (response.data.success && Object.keys(response.data.presets).length > 0) {
        setPresets(response.data.presets);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Error fetching presets from API:', error);
    }
    
    // Load from frontend config as fallback
    try {
      const module = await import('../../../config/vehicles/aiPresets');
      setPresets(module.AI_PRESETS || {});
    } catch (e) {
      console.error('Error loading presets from config:', e);
    }
    setLoading(false);
  };

  const fetchGlobalSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/presets/settings`);
      if (response.data.success) {
        setGlobalSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleTogglePreset = async (vehicleType, presetId, enabled) => {
    try {
      await axios.patch(`${API_URL}/api/admin/vehicles/presets/${presetId}?admin_id=admin`, {
        enabled: enabled
      });
      
      // Update local state
      setPresets(prev => ({
        ...prev,
        [vehicleType]: prev[vehicleType].map(p => 
          p.id === presetId ? { ...p, enabled } : p
        )
      }));
    } catch (error) {
      console.error('Error toggling preset:', error);
      // Update locally anyway
      setPresets(prev => ({
        ...prev,
        [vehicleType]: prev[vehicleType].map(p => 
          p.id === presetId ? { ...p, enabled } : p
        )
      }));
    }
  };

  const handleSaveGlobalSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/vehicles/presets/settings?admin_id=admin`, globalSettings);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditPreset = (preset) => {
    setEditingPreset({ ...preset });
    setEditDialog(true);
  };

  const handleSavePreset = async () => {
    if (!editingPreset) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/vehicles/presets/${editingPreset.id}?admin_id=admin`, editingPreset);
      
      // Update local state
      setPresets(prev => ({
        ...prev,
        [selectedVehicle]: prev[selectedVehicle].map(p => 
          p.id === editingPreset.id ? editingPreset : p
        )
      }));
      
      setEditDialog(false);
      setEditingPreset(null);
    } catch (error) {
      console.error('Error saving preset:', error);
      // Update locally anyway
      setPresets(prev => ({
        ...prev,
        [selectedVehicle]: prev[selectedVehicle].map(p => 
          p.id === editingPreset.id ? editingPreset : p
        )
      }));
      setEditDialog(false);
      setEditingPreset(null);
    } finally {
      setSaving(false);
    }
  };

  const updateEditingWeight = (weightKey, value) => {
    setEditingPreset(prev => ({
      ...prev,
      rankingWeights: {
        ...prev.rankingWeights,
        [weightKey]: value / 100
      }
    }));
  };

  const vehicleTypes = Object.keys(presets);
  const currentPresets = presets[selectedVehicle] || [];
  const VehicleIcon = VEHICLE_ICONS[selectedVehicle] || Car;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">AI Presets Management</h1>
                <p className="text-purple-100 text-sm">Manage one-tap filter bundles and ranking weights</p>
              </div>
            </div>
            <Link to="/admin/vehicles">
              <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Vehicles
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Global Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Global Preset Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">AI Presets Enabled</Label>
                  <p className="text-sm text-gray-500">Show quick filter presets to users</p>
                </div>
                <Switch
                  checked={globalSettings.presetsEnabled}
                  onCheckedChange={(checked) => setGlobalSettings(p => ({ ...p, presetsEnabled: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Preset Ranking Enabled</Label>
                  <p className="text-sm text-gray-500">Apply custom ranking weights per preset</p>
                </div>
                <Switch
                  checked={globalSettings.rankingEnabled}
                  onCheckedChange={(checked) => setGlobalSettings(p => ({ ...p, rankingEnabled: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Show Ranking Scores</Label>
                  <p className="text-sm text-gray-500">Display scores on listings (debug)</p>
                </div>
                <Switch
                  checked={globalSettings.showRankingScores}
                  onCheckedChange={(checked) => setGlobalSettings(p => ({ ...p, showRankingScores: checked }))}
                />
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSaveGlobalSettings} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Type Tabs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <VehicleIcon className="h-5 w-5" />
                Presets by Vehicle Type
              </span>
              <Badge variant="outline">
                {currentPresets.filter(p => p.enabled !== false).length}/{currentPresets.length} Active
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedVehicle} onValueChange={setSelectedVehicle}>
              <TabsList className="flex flex-wrap gap-1 h-auto mb-6">
                {vehicleTypes.map((type) => {
                  const Icon = VEHICLE_ICONS[type] || Car;
                  const count = (presets[type] || []).filter(p => p.enabled !== false).length;
                  return (
                    <TabsTrigger key={type} value={type} className="flex items-center gap-1">
                      <Icon className="h-4 w-4" />
                      <span className="capitalize">{type.replace(/-/g, ' ')}</span>
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5">{count}</Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {vehicleTypes.map((vehicleType) => (
                <TabsContent key={vehicleType} value={vehicleType}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(presets[vehicleType] || []).map((preset) => (
                      <Card 
                        key={preset.id} 
                        className={`border-2 ${preset.enabled !== false ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/50 opacity-60'}`}
                      >
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{preset.icon}</span>
                              <div>
                                <h3 className="font-semibold">{preset.name}</h3>
                                <p className="text-sm text-gray-500">{preset.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={preset.enabled !== false}
                                onCheckedChange={(checked) => handleTogglePreset(vehicleType, preset.id, checked)}
                              />
                            </div>
                          </div>

                          {/* Filter Summary */}
                          <div className="mb-3">
                            <Label className="text-xs text-gray-500 mb-1 block">Filters Applied:</Label>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(preset.filters || {}).slice(0, 4).map(([key, value]) => (
                                <Badge key={key} variant="outline" className="text-xs">
                                  {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                                </Badge>
                              ))}
                              {Object.keys(preset.filters || {}).length > 4 && (
                                <Badge variant="outline" className="text-xs">
                                  +{Object.keys(preset.filters).length - 4} more
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Ranking Weights Preview */}
                          {preset.rankingWeights && (
                            <div className="mb-3">
                              <Label className="text-xs text-gray-500 mb-1 block">Ranking Adjustments:</Label>
                              <div className="grid grid-cols-4 gap-1">
                                {Object.entries(preset.rankingWeights).slice(0, 4).map(([key, value]) => (
                                  <div key={key} className="text-center">
                                    <span className="text-lg">{WEIGHT_LABELS[key]?.icon || '📊'}</span>
                                    <p className="text-xs font-mono">{Math.round(value * 100)}%</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Country Restrictions */}
                          {preset.countryEnable && (
                            <div className="mb-3">
                              <Badge variant="secondary" className="text-xs">
                                Only: {preset.countryEnable.join(', ')}
                              </Badge>
                            </div>
                          )}

                          {/* Edit Button */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => handleEditPreset(preset)}
                          >
                            <Edit2 className="h-3 w-3 mr-2" />
                            Edit Ranking Weights
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Edit Preset Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{editingPreset?.icon}</span>
              Edit: {editingPreset?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editingPreset && (
            <div className="space-y-6 py-4">
              <p className="text-sm text-gray-600">{editingPreset.description}</p>
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Ranking Weight Adjustments</Label>
                <p className="text-xs text-gray-500 mb-4">
                  Adjust how results are ranked when this preset is active. Higher weights = more importance.
                </p>
                
                <div className="space-y-4">
                  {Object.entries(WEIGHT_LABELS).map(([key, { label, icon, description }]) => {
                    const value = (editingPreset.rankingWeights?.[key] || 0.15) * 100;
                    return (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="flex items-center gap-2">
                            <span>{icon}</span>
                            {label}
                            <span className="text-xs text-gray-400">({description})</span>
                          </Label>
                          <span className="font-mono text-sm font-medium">{Math.round(value)}%</span>
                        </div>
                        <Slider
                          value={[value]}
                          onValueChange={([v]) => updateEditingWeight(key, v)}
                          min={0}
                          max={50}
                          step={5}
                          className="w-full"
                        />
                      </div>
                    );
                  })}
                </div>
                
                {/* Total Check */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Total Weight:</span>
                    <span className={`font-mono ${
                      Math.abs(Object.values(editingPreset.rankingWeights || {}).reduce((a, b) => a + b, 0) - 1) < 0.01 
                        ? 'text-green-600' 
                        : 'text-red-600'
                    }`}>
                      {Math.round(Object.values(editingPreset.rankingWeights || {}).reduce((a, b) => a + b, 0) * 100)}%
                      {Math.abs(Object.values(editingPreset.rankingWeights || {}).reduce((a, b) => a + b, 0) - 1) < 0.01 
                        ? ' ✓' 
                        : ' (should be 100%)'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePreset} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIPresetsManagementPage;
